import { ItemCategory } from "../types";

/**
 * Catalog brands by category — sell BrandSelector is select-only from this list.
 * Keep names buyer-facing (canonical spelling). Empty arrays = no brand field
 * expected for that category (fieldConfigs.brand should be false).
 */
export const brandSuggestionsByCategory: Record<ItemCategory, string[]> = {
  electronics: [
    "LG",
    "Samsung",
    "Sony",
    "Panasonic",
    "TCL",
    "Hisense",
    "Sharp",
    "Toshiba",
    "Philips",
    "Haier Thermocool",
    "Scanfrost",
    "Midea",
    "Nexus",
    "Skyrun",
    "Polystar",
    "Bruhm",
    "Royal",
    "Nasco",
    "Maxi",
    "Century",
    "Daikin",
    "Carrier",
    "Xiaomi",
    "Binatone",
  ],
  power_solutions: [
    "Firman",
    "Honda",
    "Elepaq",
    "Lutian",
    "Sumec",
    "Yamaha",
    "Mikano",
    "Thermocool",
    "Haier Thermocool",
    "Luminous",
    "Su-Kam",
    "Mercury",
    "Felicity",
    "Nexus",
    "Maxi",
    "Century",
  ],
  kitchen: [
    "Haier Thermocool",
    "LG",
    "Samsung",
    "Scanfrost",
    "Midea",
    "Sharp",
    "Hisense",
    "Binatone",
    "Nexus",
    "Skyrun",
    "Polystar",
    "Bruhm",
    "Nasco",
    "Maxi",
    "Philips",
    "Kenwood",
    "Moulinex",
    "Black+Decker",
    "Russell Hobbs",
    "Nutribullet",
    "Bosch",
    "Whirlpool",
  ],
  furniture: [
    "Ikea",
    "Vitafoam",
    "Mouka",
    "Vono",
    "Kraftmatic",
    "Duroflex",
    "Godrej",
  ],
  bedding: [
    "Mouka",
    "Vitafoam",
    "Vono",
    "Duroflex",
    "Kraftmatic",
    "Dunlopillo",
  ],
  fitness: [
    "ProForm",
    "NordicTrack",
    "Bowflex",
    "Sole",
    "Schwinn",
    "Raleigh",
    "Trek",
    "Giant",
  ],
  fashion: ["Singer", "Brother", "Janome", "Bernina", "Pfaff", "Elna"],
  decor: [],
  outdoor: [],
  tools: ["Bosch", "Black+Decker", "Stanley", "Makita", "DeWalt", "Ryobi"],
  lighting: ["Philips", "Osram", "GE", "Sylvania", "Cree"],
  baby: ["Graco", "Fisher-Price", "Chicco", "Evenflo", "Safety 1st", "Stokke"],
  digital_wears: ["Apple", "Samsung", "Garmin", "Fitbit", "Xiaomi", "Amazfit"],
  gaming: ["PlayStation", "Xbox", "Nintendo", "Sega", "Steam"],
  personal_computers: [
    "HP",
    "Dell",
    "Lenovo",
    "Apple",
    "Asus",
    "Acer",
    "Microsoft",
  ],
  phones_tabs: [
    "Apple",
    "Samsung",
    "Tecno",
    "Infinix",
    "Xiaomi",
    "Oppo",
    "Vivo",
    "Itel",
    "Nokia",
    "Google",
    "OnePlus",
    "Redmi",
  ],
  other: [],
};

/** Shared by speaker, soundbar, sound system, and home theatre (D2). */
const AUDIO_BRANDS = [
  "JBL",
  "Edifier",
  "Bose",
  "Sony",
  "LG",
  "Samsung",
  "Hisense",
  "Harman Kardon",
  "Zealot",
  "Oraimo",
  "Xiaomi",
  "Philips",
  "TCL",
] as const;

/**
 * Subcategory lists replace the category list when the key is present.
 * An empty list means brand is not asked (D6). Missing keys fall back.
 */
const brandSuggestionsBySubcategory: Record<string, readonly string[]> = {
  speaker: AUDIO_BRANDS,
  soundbar: AUDIO_BRANDS,
  "sound-system": AUDIO_BRANDS,
  "home-theatre-system": AUDIO_BRANDS,
  camera: ["Canon", "Nikon", "Sony", "Fujifilm", "Panasonic", "GoPro", "DJI"],
  "router-modem": [
    "TP-Link",
    "Huawei",
    "D-Link",
    "Tenda",
    "Mercusys",
    "Xiaomi",
    "Asus",
    "MTN",
    "Airtel",
  ],
  "vr-headset": ["Meta", "Apple", "Sony", "Pico", "HTC"],
  "snooker-board": [],
  mannequin: [],
};

function dedupeBrands(list: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const brand of list) {
    const key = brand.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(brand);
  }
  return out;
}

/**
 * Brand catalog for a category, or the subcategory list when one is registered.
 * Empty subcategory lists are intentional (do not fall back).
 */
export function getBrandSuggestions(
  category: ItemCategory | string | null | undefined,
  subcategoryId?: string | null,
): string[] {
  const subKey = subcategoryId?.trim() ?? "";
  if (
    subKey &&
    Object.prototype.hasOwnProperty.call(brandSuggestionsBySubcategory, subKey)
  ) {
    return dedupeBrands(brandSuggestionsBySubcategory[subKey]);
  }
  if (!category) return [];
  const list = brandSuggestionsByCategory[category as ItemCategory] || [];
  return dedupeBrands(list);
}

/**
 * Filter brand catalog by search term (does not invent custom brands).
 */
export function searchBrands(
  searchTerm: string,
  category: ItemCategory | string | null | undefined,
  subcategoryId?: string | null,
): string[] {
  const suggestions = getBrandSuggestions(category, subcategoryId);
  if (!searchTerm.trim()) return suggestions.slice(0, 12);

  const normalizedSearch = searchTerm.toLowerCase();
  return suggestions
    .filter((brand) => brand.toLowerCase().includes(normalizedSearch))
    .slice(0, 12);
}
